'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, CheckCircle2, Circle, ChevronRight, ChevronDown, ChevronUp, Building2, FileText, ShieldCheck, Star, ThumbsUp, Search, Plus, User, Mail, MapPin, Calendar, Truck, Edit2, XCircle, RefreshCw, Download, CheckSquare, AlertTriangle } from 'lucide-react';
import { type Supplier } from '../../components/suppliersData';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

type OnboardingStepStatus = 'completed' | 'in_progress' | 'pending' | 'failed';

interface OnboardingStep {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  status: OnboardingStepStatus;
  completedAt?: string;
  notes?: string;
}

interface OnboardingSupplier {
  id: string;
  supplierCode: string;
  name: string;
  supplierType: string;
  categories: string[];
  country: string;
  city?: string;
  email?: string;
  phone?: string;
  contactPerson?: string;
  startedAt: string;
  updatedAt: string;
  overallStatus: 'in_progress' | 'completed' | 'on_hold' | 'rejected';
  completionPct: number;
  steps: OnboardingStep[];
  assignedTo?: string;
  priority: 'normal' | 'high' | 'urgent';
}

// ─── Mock Onboarding Data ─────────────────────────────────────────────────────

const ONBOARDING_STEPS_TEMPLATE: Omit<OnboardingStep, 'status' | 'completedAt' | 'notes'>[] = [
  { id: 'basic_info', label: 'Basic Information', description: 'Company details, address, contact info', icon: Building2 },
  { id: 'documents', label: 'Documents', description: 'Registration, TIN, licences, certificates', icon: FileText },
  { id: 'compliance', label: 'Compliance Review', description: 'Document verification and compliance check', icon: ShieldCheck },
  { id: 'evaluation', label: 'Evaluation', description: 'Supplier capability and performance assessment', icon: Star },
  { id: 'approval', label: 'Approval', description: 'Final approval and activation', icon: ThumbsUp },
];

const MOCK_ONBOARDING: OnboardingSupplier[] = [
  {
    id: 'ob-001',
    supplierCode: 'SUP-00012',
    name: 'Nile Agro Supplies Ltd',
    supplierType: 'Distributor',
    categories: ['Feed', 'Feed Supplements'],
    country: 'Uganda',
    city: 'Jinja',
    email: 'info@nileagro.co.ug',
    phone: '+256 772 345 678',
    contactPerson: 'Opio Samuel',
    startedAt: '2026-08-20',
    updatedAt: '2026-09-03',
    overallStatus: 'in_progress',
    completionPct: 60,
    assignedTo: 'Nakato Prossy',
    priority: 'high',
    steps: [
      { id: 'basic_info', label: 'Basic Information', description: 'Company details, address, contact info', icon: Building2, status: 'completed', completedAt: '2026-08-21' },
      { id: 'documents', label: 'Documents', description: 'Registration, TIN, licences, certificates', icon: FileText, status: 'completed', completedAt: '2026-08-25' },
      { id: 'compliance', label: 'Compliance Review', description: 'Document verification and compliance check', icon: ShieldCheck, status: 'in_progress', notes: 'TIN certificate under verification' },
      { id: 'evaluation', label: 'Evaluation', description: 'Supplier capability and performance assessment', icon: Star, status: 'pending' },
      { id: 'approval', label: 'Approval', description: 'Final approval and activation', icon: ThumbsUp, status: 'pending' },
    ],
  },
  {
    id: 'ob-002',
    supplierCode: 'SUP-00013',
    name: 'Pearl Veterinary Supplies',
    supplierType: 'Veterinary Supplier',
    categories: ['Vaccines', 'Veterinary Medicine'],
    country: 'Uganda',
    city: 'Entebbe',
    email: 'orders@pearlvet.co.ug',
    phone: '+256 701 456 789',
    contactPerson: 'Dr. Akello Faith',
    startedAt: '2026-08-28',
    updatedAt: '2026-09-01',
    overallStatus: 'in_progress',
    completionPct: 40,
    assignedTo: 'Mugisha Peter',
    priority: 'urgent',
    steps: [
      { id: 'basic_info', label: 'Basic Information', description: 'Company details, address, contact info', icon: Building2, status: 'completed', completedAt: '2026-08-29' },
      { id: 'documents', label: 'Documents', description: 'Registration, TIN, licences, certificates', icon: FileText, status: 'in_progress', notes: 'Awaiting veterinary licence upload' },
      { id: 'compliance', label: 'Compliance Review', description: 'Document verification and compliance check', icon: ShieldCheck, status: 'pending' },
      { id: 'evaluation', label: 'Evaluation', description: 'Supplier capability and performance assessment', icon: Star, status: 'pending' },
      { id: 'approval', label: 'Approval', description: 'Final approval and activation', icon: ThumbsUp, status: 'pending' },
    ],
  },
  {
    id: 'ob-003',
    supplierCode: 'SUP-00014',
    name: 'Kampala Packaging Solutions',
    supplierType: 'Manufacturer',
    categories: ['Egg Trays', 'Packaging'],
    country: 'Uganda',
    city: 'Kampala',
    email: 'sales@kpsolutions.co.ug',
    phone: '+256 700 567 890',
    contactPerson: 'Ssemakula Robert',
    startedAt: '2026-09-01',
    updatedAt: '2026-09-04',
    overallStatus: 'in_progress',
    completionPct: 80,
    assignedTo: 'Nakato Prossy',
    priority: 'normal',
    steps: [
      { id: 'basic_info', label: 'Basic Information', description: 'Company details, address, contact info', icon: Building2, status: 'completed', completedAt: '2026-09-01' },
      { id: 'documents', label: 'Documents', description: 'Registration, TIN, licences, certificates', icon: FileText, status: 'completed', completedAt: '2026-09-02' },
      { id: 'compliance', label: 'Compliance Review', description: 'Document verification and compliance check', icon: ShieldCheck, status: 'completed', completedAt: '2026-09-03' },
      { id: 'evaluation', label: 'Evaluation', description: 'Supplier capability and performance assessment', icon: Star, status: 'in_progress', notes: 'Evaluation scheduled for 6 Sep 2026' },
      { id: 'approval', label: 'Approval', description: 'Final approval and activation', icon: ThumbsUp, status: 'pending' },
    ],
  },
  {
    id: 'ob-004',
    supplierCode: 'SUP-00015',
    name: 'Sunrise Hatcheries Uganda',
    supplierType: 'Hatchery',
    categories: ['Day-Old Chicks', 'Breeding Stock'],
    country: 'Uganda',
    city: 'Mbarara',
    email: 'info@sunrisehatch.co.ug',
    phone: '+256 782 678 901',
    contactPerson: 'Tumwine Joshua',
    startedAt: '2026-07-15',
    updatedAt: '2026-08-10',
    overallStatus: 'completed',
    completionPct: 100,
    assignedTo: 'Mugisha Peter',
    priority: 'normal',
    steps: [
      { id: 'basic_info', label: 'Basic Information', description: 'Company details, address, contact info', icon: Building2, status: 'completed', completedAt: '2026-07-16' },
      { id: 'documents', label: 'Documents', description: 'Registration, TIN, licences, certificates', icon: FileText, status: 'completed', completedAt: '2026-07-22' },
      { id: 'compliance', label: 'Compliance Review', description: 'Document verification and compliance check', icon: ShieldCheck, status: 'completed', completedAt: '2026-07-30' },
      { id: 'evaluation', label: 'Evaluation', description: 'Supplier capability and performance assessment', icon: Star, status: 'completed', completedAt: '2026-08-05' },
      { id: 'approval', label: 'Approval', description: 'Final approval and activation', icon: ThumbsUp, status: 'completed', completedAt: '2026-08-10' },
    ],
  },
  {
    id: 'ob-005',
    supplierCode: 'SUP-00016',
    name: 'Greenfield Farm Inputs',
    supplierType: 'Wholesaler',
    categories: ['Disinfectants', 'Chemicals', 'PPE'],
    country: 'Uganda',
    city: 'Gulu',
    email: 'procurement@greenfield.co.ug',
    phone: '+256 756 789 012',
    contactPerson: 'Onen Charles',
    startedAt: '2026-08-10',
    updatedAt: '2026-08-18',
    overallStatus: 'on_hold',
    completionPct: 20,
    assignedTo: 'Nakato Prossy',
    priority: 'normal',
    steps: [
      { id: 'basic_info', label: 'Basic Information', description: 'Company details, address, contact info', icon: Building2, status: 'completed', completedAt: '2026-08-11' },
      { id: 'documents', label: 'Documents', description: 'Registration, TIN, licences, certificates', icon: FileText, status: 'failed', notes: 'Business registration expired. Supplier notified.' },
      { id: 'compliance', label: 'Compliance Review', description: 'Document verification and compliance check', icon: ShieldCheck, status: 'pending' },
      { id: 'evaluation', label: 'Evaluation', description: 'Supplier capability and performance assessment', icon: Star, status: 'pending' },
      { id: 'approval', label: 'Approval', description: 'Final approval and activation', icon: ThumbsUp, status: 'pending' },
    ],
  },
];

// ─── Step Status Config ───────────────────────────────────────────────────────

const STEP_STATUS_CONFIG: Record<OnboardingStepStatus, { icon: React.ElementType; color: string; bg: string; label: string; ring: string }> = {
  completed: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-100', label: 'Completed', ring: 'ring-green-300' },
  in_progress: { icon: RefreshCw, color: 'text-blue-600', bg: 'bg-blue-100', label: 'In Progress', ring: 'ring-blue-300' },
  pending: { icon: Circle, color: 'text-gray-400', bg: 'bg-gray-100', label: 'Pending', ring: 'ring-gray-200' },
  failed: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-100', label: 'Blocked', ring: 'ring-red-300' },
};

const OVERALL_STATUS_CONFIG = {
  in_progress: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500', label: 'In Progress' },
  completed: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500', label: 'Completed' },
  on_hold: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500', label: 'On Hold' },
  rejected: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500', label: 'Rejected' },
};

const PRIORITY_CONFIG = {
  normal: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Normal' },
  high: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'High' },
  urgent: { bg: 'bg-red-100', text: 'text-red-700', label: 'Urgent' },
};

// ─── Step Tracker (visual stepper) ───────────────────────────────────────────

function StepTracker({ steps }: { steps: OnboardingStep[] }) {
  return (
    <div className="flex items-start gap-0 w-full">
      {steps.map((step, idx) => {
        const cfg = STEP_STATUS_CONFIG[step.status];
        const StepIcon = cfg.icon;
        const isLast = idx === steps.length - 1;
        return (
          <div key={step.id} className="flex items-start flex-1 min-w-0">
            <div className="flex flex-col items-center flex-1 min-w-0">
              {/* Circle */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ring-2 ${cfg.bg} ${cfg.ring}`}>
                <StepIcon size={14} className={cfg.color} />
              </div>
              {/* Label */}
              <div className="mt-1.5 text-center px-1">
                <div className={`text-xs font-semibold leading-tight ${cfg.color}`}>{step.label}</div>
              </div>
            </div>
            {/* Connector */}
            {!isLast && (
              <div className="flex-shrink-0 mt-4 w-full flex-1 max-w-[40px]">
                <div className={`h-0.5 w-full ${step.status === 'completed' ? 'bg-green-400' : 'bg-gray-200'}`} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Progress Bar ─────────────────────────────────────────────────────────────

function ProgressBar({ pct, status }: { pct: number; status: OnboardingSupplier['overallStatus'] }) {
  const color = status === 'completed' ? 'bg-green-500' : status === 'on_hold' ? 'bg-amber-400' : status === 'rejected' ? 'bg-red-400' : 'bg-blue-500';
  return (
    <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
      <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

// ─── Onboarding Card ──────────────────────────────────────────────────────────

function OnboardingCard({ supplier, onSelect }: { supplier: OnboardingSupplier; onSelect: (s: OnboardingSupplier) => void }) {
  const statusCfg = OVERALL_STATUS_CONFIG[supplier.overallStatus];
  const priorityCfg = PRIORITY_CONFIG[supplier.priority];
  const currentStep = supplier.steps.find(s => s.status === 'in_progress') || supplier.steps.find(s => s.status === 'failed');

  return (
    <div
      className="bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md hover:border-orange-200 transition-all cursor-pointer group"
      onClick={() => onSelect(supplier)}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
            <Truck size={18} className="text-orange-600" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-900 truncate group-hover:text-orange-700 transition-colors">{supplier.name}</div>
            <div className="text-xs text-gray-400">{supplier.supplierCode} · {supplier.supplierType}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${priorityCfg.bg} ${priorityCfg.text}`}>
            {priorityCfg.label}
          </span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${statusCfg.bg} ${statusCfg.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
            {statusCfg.label}
          </span>
        </div>
      </div>

      {/* Categories */}
      <div className="flex flex-wrap gap-1 mb-4">
        {supplier.categories.slice(0, 3).map(cat => (
          <span key={cat} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-lg">{cat}</span>
        ))}
        {supplier.categories.length > 3 && (
          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-lg">+{supplier.categories.length - 3}</span>
        )}
      </div>

      {/* Step Tracker */}
      <div className="mb-4">
        <StepTracker steps={supplier.steps} />
      </div>

      {/* Progress */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-gray-500">Completion</span>
          <span className="text-xs font-bold text-gray-700">{supplier.completionPct}%</span>
        </div>
        <ProgressBar pct={supplier.completionPct} status={supplier.overallStatus} />
      </div>

      {/* Current Step Note */}
      {currentStep?.notes && (
        <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-100 rounded-xl mb-3">
          <AlertTriangle size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700 leading-snug">{currentStep.notes}</p>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-50">
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <User size={11} />
          <span>{supplier.assignedTo ?? 'Unassigned'}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Calendar size={11} />
          <span>Started {supplier.startedAt}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────────

function DetailPanel({ supplier, onClose }: { supplier: OnboardingSupplier; onClose: () => void }) {
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const statusCfg = OVERALL_STATUS_CONFIG[supplier.overallStatus];

  const STEP_CHECKLIST: Record<string, string[]> = {
    basic_info: [
      'Company legal name entered',
      'Trading name provided',
      'Business type selected',
      'Physical address captured',
      'Primary contact person added',
      'Email and phone verified',
      'Country and district set',
    ],
    documents: [
      'Certificate of Incorporation uploaded',
      'Business Registration uploaded',
      'TIN Certificate uploaded',
      'Trading Licence uploaded',
      'Tax Clearance Certificate uploaded',
      'Bank Confirmation Letter uploaded',
      'Company Profile uploaded',
    ],
    compliance: [
      'All mandatory documents verified',
      'Document expiry dates checked',
      'TIN number validated',
      'Business registration confirmed active',
      'No blacklist flags found',
      'Compliance officer sign-off obtained',
    ],
    evaluation: [
      'Supplier capability assessment completed',
      'Product/service quality reviewed',
      'Pricing competitiveness evaluated',
      'Delivery capacity confirmed',
      'References checked',
      'Site visit conducted (if required)',
      'Evaluation score recorded',
    ],
    approval: [
      'Compliance review approved',
      'Evaluation score meets threshold',
      'Procurement manager approval obtained',
      'Supplier activated in system',
      'Welcome communication sent',
      'Supplier added to approved vendor list',
    ],
  };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden flex flex-col h-full">
      {/* Header */}
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-orange-100 flex items-center justify-center">
              <Truck size={20} className="text-orange-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">{supplier.name}</h2>
              <p className="text-xs text-gray-400">{supplier.supplierCode} · {supplier.supplierType}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <XCircle size={16} />
          </button>
        </div>

        {/* Status + Progress */}
        <div className="flex items-center gap-2 mb-3">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusCfg.bg} ${statusCfg.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
            {statusCfg.label}
          </span>
          <span className="text-xs text-gray-400">{supplier.completionPct}% complete</span>
        </div>
        <ProgressBar pct={supplier.completionPct} status={supplier.overallStatus} />

        {/* Quick Info */}
        <div className="grid grid-cols-2 gap-2 mt-4">
          {supplier.contactPerson && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <User size={11} className="text-gray-400" />
              <span>{supplier.contactPerson}</span>
            </div>
          )}
          {supplier.city && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <MapPin size={11} className="text-gray-400" />
              <span>{supplier.city}, {supplier.country}</span>
            </div>
          )}
          {supplier.email && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 col-span-2">
              <Mail size={11} className="text-gray-400" />
              <span className="truncate">{supplier.email}</span>
            </div>
          )}
          {supplier.assignedTo && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 col-span-2">
              <CheckSquare size={11} className="text-gray-400" />
              <span>Assigned to: <span className="font-semibold text-gray-700">{supplier.assignedTo}</span></span>
            </div>
          )}
        </div>
      </div>

      {/* Steps */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Onboarding Checklist</h3>
        {supplier.steps.map((step, idx) => {
          const cfg = STEP_STATUS_CONFIG[step.status];
          const StepIcon = cfg.icon;
          const isExpanded = expandedStep === step.id;
          const checklist = STEP_CHECKLIST[step.id] ?? [];
          const completedChecks = step.status === 'completed' ? checklist.length : step.status === 'in_progress' ? Math.floor(checklist.length * 0.5) : 0;

          return (
            <div key={step.id} className={`border rounded-xl overflow-hidden transition-all ${step.status === 'in_progress' ? 'border-blue-200 bg-blue-50/30' : step.status === 'failed' ? 'border-red-200 bg-red-50/20' : step.status === 'completed' ? 'border-green-100 bg-green-50/20' : 'border-gray-100 bg-white'}`}>
              <button
                className="w-full flex items-center gap-3 p-3.5 text-left hover:bg-white/60 transition-colors"
                onClick={() => setExpandedStep(isExpanded ? null : step.id)}
              >
                {/* Step number */}
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${cfg.bg} ${cfg.color}`}>
                  {idx + 1}
                </div>
                {/* Icon */}
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                  <StepIcon size={13} className={cfg.color} />
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-800">{step.label}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{step.description}</p>
                  {step.completedAt && (
                    <p className="text-xs text-green-600 mt-0.5 font-medium">✓ Completed {step.completedAt}</p>
                  )}
                  {step.notes && (
                    <p className="text-xs text-amber-600 mt-0.5">{step.notes}</p>
                  )}
                </div>
                {/* Checklist count */}
                <div className="text-xs text-gray-400 flex-shrink-0 mr-1">
                  {completedChecks}/{checklist.length}
                </div>
                {isExpanded ? <ChevronUp size={14} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />}
              </button>

              {/* Expanded Checklist */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100/80">
                  <div className="pt-3 space-y-2">
                    {checklist.map((item, i) => {
                      const done = i < completedChecks;
                      return (
                        <div key={i} className="flex items-center gap-2.5">
                          {done
                            ? <CheckCircle2 size={13} className="text-green-500 flex-shrink-0" />
                            : <Circle size={13} className="text-gray-300 flex-shrink-0" />
                          }
                          <span className={`text-xs ${done ? 'text-gray-600 line-through' : 'text-gray-500'}`}>{item}</span>
                        </div>
                      );
                    })}
                  </div>
                  {/* Action buttons per step */}
                  <div className="mt-3 flex gap-2">
                    {step.status === 'in_progress' && (
                      <button className="flex-1 py-1.5 px-3 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                        Mark Complete
                      </button>
                    )}
                    {step.status === 'pending' && (
                      <button className="flex-1 py-1.5 px-3 bg-orange-500 text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors">
                        Start Step
                      </button>
                    )}
                    {step.status === 'failed' && (
                      <button className="flex-1 py-1.5 px-3 bg-red-500 text-white text-xs font-semibold rounded-lg hover:bg-red-600 transition-colors">
                        Retry Step
                      </button>
                    )}
                    {step.status === 'completed' && (
                      <button className="flex-1 py-1.5 px-3 bg-gray-100 text-gray-600 text-xs font-semibold rounded-lg hover:bg-gray-200 transition-colors">
                        View Details
                      </button>
                    )}
                    <button className="py-1.5 px-3 bg-gray-100 text-gray-600 text-xs font-semibold rounded-lg hover:bg-gray-200 transition-colors">
                      Add Note
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Actions */}
      <div className="p-4 border-t border-gray-100 flex gap-2">
        <Link
          href={`/suppliers/${supplier.id}`}
          className="flex-1 py-2 px-3 bg-orange-500 text-white text-xs font-semibold rounded-xl hover:bg-orange-600 transition-colors text-center"
        >
          View Full Profile
        </Link>
        <button className="py-2 px-3 bg-gray-100 text-gray-600 text-xs font-semibold rounded-xl hover:bg-gray-200 transition-colors">
          <Edit2 size={13} />
        </button>
        <button className="py-2 px-3 bg-gray-100 text-gray-600 text-xs font-semibold rounded-xl hover:bg-gray-200 transition-colors">
          <Download size={13} />
        </button>
      </div>
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color: string }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-4">
      <div className="text-xs font-semibold text-gray-400 mb-1">{label}</div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SupplierOnboardingContent() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedSupplier, setSelectedSupplier] = useState<OnboardingSupplier | null>(null);

  const filtered = MOCK_ONBOARDING.filter(s => {
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.supplierCode.toLowerCase().includes(search.toLowerCase()) ||
      s.categories.some(c => c.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = statusFilter === 'all' || s.overallStatus === statusFilter;
    return matchSearch && matchStatus;
  });

  // KPI counts
  const total = MOCK_ONBOARDING.length;
  const inProgress = MOCK_ONBOARDING.filter(s => s.overallStatus === 'in_progress').length;
  const completed = MOCK_ONBOARDING.filter(s => s.overallStatus === 'completed').length;
  const onHold = MOCK_ONBOARDING.filter(s => s.overallStatus === 'on_hold').length;
  const urgent = MOCK_ONBOARDING.filter(s => s.priority === 'urgent').length;

  return (
    <div className="flex flex-col h-full min-h-0 bg-gray-50">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-100 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Link href="/suppliers" className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
              <ArrowLeft size={16} />
            </Link>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Supplier Onboarding</h1>
              <p className="text-xs text-gray-400 mt-0.5">Track supplier registration progress through each onboarding stage</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
              <Download size={13} />
              Export
            </button>
            <Link
              href="/suppliers/new"
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors"
            >
              <Plus size={13} />
              Start Onboarding
            </Link>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {/* KPI Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <KpiCard label="Total Onboarding" value={total} sub="All time" color="text-gray-800" />
          <KpiCard label="In Progress" value={inProgress} sub="Active workflows" color="text-blue-600" />
          <KpiCard label="Completed" value={completed} sub="Fully approved" color="text-green-600" />
          <KpiCard label="On Hold" value={onHold} sub="Awaiting action" color="text-amber-600" />
          <KpiCard label="Urgent" value={urgent} sub="Needs attention" color="text-red-600" />
        </div>

        {/* Workflow Steps Legend */}
        <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-6">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Onboarding Workflow</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {ONBOARDING_STEPS_TEMPLATE.map((step, idx) => {
              const StepIcon = step.icon;
              return (
                <React.Fragment key={step.id}>
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
                    <div className="w-6 h-6 rounded-lg bg-orange-100 flex items-center justify-center">
                      <StepIcon size={12} className="text-orange-600" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-700">{step.label}</div>
                      <div className="text-xs text-gray-400 hidden sm:block">{step.description}</div>
                    </div>
                  </div>
                  {idx < ONBOARDING_STEPS_TEMPLATE.length - 1 && (
                    <ChevronRight size={14} className="text-gray-300 flex-shrink-0" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          {/* Legend */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-50 flex-wrap">
            {Object.entries(STEP_STATUS_CONFIG).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <div key={key} className="flex items-center gap-1.5">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${cfg.bg}`}>
                    <Icon size={10} className={cfg.color} />
                  </div>
                  <span className="text-xs text-gray-500">{cfg.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search suppliers..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {(['all', 'in_progress', 'completed', 'on_hold', 'rejected'] as const).map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-xl transition-colors ${statusFilter === s ? 'bg-orange-500 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              >
                {s === 'all' ? 'All' : OVERALL_STATUS_CONFIG[s]?.label ?? s}
              </button>
            ))}
          </div>
        </div>

        {/* Main Layout: Cards + Detail Panel */}
        <div className={`grid gap-5 ${selectedSupplier ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'}`}>
          {/* Cards Column */}
          <div className={`space-y-4 ${selectedSupplier ? '' : 'col-span-full sm:col-span-2 xl:col-span-3'}`}>
            {filtered.length === 0 ? (
              <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center">
                <Truck size={36} className="text-gray-200 mx-auto mb-3" />
                <p className="text-sm font-semibold text-gray-400">No onboarding records found</p>
                <p className="text-xs text-gray-300 mt-1">Try adjusting your search or filters</p>
                <Link href="/suppliers/new" className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 text-xs font-semibold text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors">
                  <Plus size={12} />
                  Start New Onboarding
                </Link>
              </div>
            ) : (
              <div className={`grid gap-4 ${selectedSupplier ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'}`}>
                {filtered.map(s => (
                  <OnboardingCard
                    key={s.id}
                    supplier={s}
                    onSelect={sup => setSelectedSupplier(selectedSupplier?.id === sup.id ? null : sup)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Detail Panel */}
          {selectedSupplier && (
            <div className="lg:sticky lg:top-0 lg:max-h-[calc(100vh-180px)]">
              <DetailPanel supplier={selectedSupplier} onClose={() => setSelectedSupplier(null)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
